import { DownloadOutlined } from '@ant-design/icons';
import { Button, Card, Col, DatePicker, Descriptions, Row, Skeleton, Space, Statistic, Table, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { downloadFile, errorMessage } from '../../api/client';
import { useLedger } from '../../api/hooks';
import type { LedgerEntry } from '../../types';
import { apiDate, date, money } from '../../utils/format';
import { useTranslation } from 'react-i18next';

const TYPE_COLOR: Record<string, string> = {
  Investment: 'blue',
  Withdrawal: 'orange',
  ProfitPayment: 'green',
};

export default function LedgerTab({ investorId }: { investorId: number }) {
  const { t } = useTranslation();
  const { data, isLoading } = useLedger(investorId);
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([dayjs().startOf('year'), dayjs()]);

  // The statement is the one PDF offered here: it covers every investment, withdrawal and profit
  // payment in the period, so the per-transaction receipts would only repeat it a row at a time.
  const downloadStatement = () =>
    downloadFile(
      `/reports/investor-statement/${investorId}?from=${apiDate(range[0])}&to=${apiDate(range[1])}&format=pdf`,
      `ledger_statement_${apiDate(range[0])}_to_${apiDate(range[1])}.pdf`,
    ).catch((err) => message.error(errorMessage(err)));

  if (isLoading || !data) return <Skeleton active />;

  return (
    <>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <span>Statement period:</span>
          <DatePicker.RangePicker
            value={range}
            allowClear={false}
            onChange={(values) => values && setRange(values as [dayjs.Dayjs, dayjs.Dayjs])}
          />
          <Button type="primary" icon={<DownloadOutlined />} onClick={downloadStatement}>
            Download ledger statement (PDF)
          </Button>
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12} lg={6}>
          <Card>
            {/* Gross capital paid in: deposits only, before any withdrawal or profit. */}
            <Statistic title={t('finance.totalInvestment')} value={money(data.totalDeposited)} prefix="PKR" />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={6}>
          <Card>
            <Statistic
              title={t('finance.totalInvestmentRemaining')}
              value={money(data.currentPrincipal)}
              prefix="PKR"
            />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={6}>
          <Card>
            <Statistic title={t('finance.totalProfitRemaining')} value={money(data.outstandingProfit)} prefix="PKR" />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={6}>
          <Card>
            <Statistic
              title={t('dashboard.investmentPlusProfit')}
              value={money(data.totalLiability)}
              prefix="PKR"
              valueStyle={{ color: '#FE9F43' }}
            />
          </Card>
        </Col>
      </Row>

      <Descriptions bordered size="small" column={{ xs: 1, md: 2 }} style={{ marginTop: 16 }}>
        <Descriptions.Item label={t('dashboard.firstInvestment')}>{date(data.firstInvestmentDate)}</Descriptions.Item>
        <Descriptions.Item label={t('table.asOf')}>{date(data.asOf)}</Descriptions.Item>
        <Descriptions.Item label={t('dashboard.initialDeposit')}>{money(data.initialDeposit)}</Descriptions.Item>
        <Descriptions.Item label={t('dashboard.additionalDeposits')}>{money(data.additionalDeposits)}</Descriptions.Item>
        <Descriptions.Item label={t('dashboard.totalDeposited')}>{money(data.totalDeposited)}</Descriptions.Item>
        <Descriptions.Item label={t('finance.totalWithdrawn')}>{money(data.totalWithdrawn)}</Descriptions.Item>
        <Descriptions.Item label={t('finance.totalProfit')}>{money(data.totalAccruedProfit)}</Descriptions.Item>
        <Descriptions.Item label="Total paid profit">{money(data.totalPaidProfit)}</Descriptions.Item>
      </Descriptions>

      <Card title={t('table.transactions')} size="small" style={{ marginTop: 16 }}>
        <Table<LedgerEntry>
          rowKey={(row, index) => `${row.date}-${row.type}-${index}`}
          size="small"
          dataSource={data.entries}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          columns={[
            { title: t('common.date'), dataIndex: 'date', render: date },
            {
              title: t('common.type'),
              dataIndex: 'type',
              render: (value: string) => <Tag color={TYPE_COLOR[value] ?? 'default'}>{value}</Tag>,
            },
            { title: t('party.description'), dataIndex: 'description' },
            { title: t('table.debit'), dataIndex: 'debit', align: 'right', render: (v: number) => (v ? money(v) : '—') },
            { title: t('table.credit'), dataIndex: 'credit', align: 'right', render: (v: number) => (v ? money(v) : '—') },
            { title: 'Investment', dataIndex: 'runningPrincipal', align: 'right', render: money },
          ]}
        />
      </Card>
    </>
  );
}
